import React, { useEffect, useState, useRef } from 'react';

const CustomCursor = () => {
  const cursorRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [cursorText, setCursorText] = useState('');
  
  // Mouse position with smooth interpolation
  const mousePos = useRef({ x: 0, y: 0 });
  const cursorPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Check if device is desktop (has mouse)
    const checkDesktop = () => {
      setIsDesktop(window.matchMedia('(min-width: 768px) and (pointer: fine)').matches);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;

    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    // Check for hoverable elements
    const handleMouseOver = (e) => {
      const target = e.target;
      const isInteractive = 
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('[data-cursor="pointer"]') ||
        window.getComputedStyle(target).cursor === 'pointer';
      
      const cursorTextAttr = target.getAttribute('data-cursor-text') || 
                            target.closest('[data-cursor-text]')?.getAttribute('data-cursor-text');
      
      if (cursorTextAttr) {
        setCursorText(cursorTextAttr);
        setIsHovering(true);
      } else if (isInteractive) {
        setIsHovering(true);
        setCursorText('');
      } else {
        setIsHovering(false);
        setCursorText('');
      }
    };

    // Animation loop for smooth cursor movement
    let animationFrameId;
    const animate = () => {
      // Smooth interpolation for cursor (smooth follow effect)
      const easing = 0.15;
      cursorPos.current.x += (mousePos.current.x - cursorPos.current.x) * easing;
      cursorPos.current.y += (mousePos.current.y - cursorPos.current.y) * easing;

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${cursorPos.current.x}px, ${cursorPos.current.y}px) translate(-50%, -50%)`;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseover', handleMouseOver);
    
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseover', handleMouseOver);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDesktop]);

  if (!isDesktop) return null;

  return (
    <>
      {/* Outer cursor ring - with mix-blend-mode for contrast effect */}
      <div
        ref={cursorRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999] will-change-transform"
        style={{
          mixBlendMode: 'difference',
        }}
      >
        <div 
          className={`
            flex items-center justify-center
            rounded-full bg-white
            transition-all duration-300 ease-out
            ${isHovering ? 'w-20 h-20' : 'w-10 h-10'}
            ${isClicking ? 'scale-75' : 'scale-100'}
          `}
        >
          {cursorText && (
            <span 
              className="text-black text-xs font-bold uppercase tracking-wider"
              style={{ mixBlendMode: 'difference' }}
            >
              {cursorText}
            </span>
          )}
        </div>
      </div>

    </>
  );
};

export default CustomCursor;
